import { BrowserModule } from '@angular/platform-browser';
import { NgModule, ErrorHandler, Injector, inject, provideAppInitializer } from '@angular/core';
import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { DatePipe, TitleCasePipe, registerLocaleData } from '@angular/common';

import { AppComponent } from './app.component';
import { AppInjector } from './services/app-injector.service';

import { ToastNoAnimationModule } from 'ngx-toastr';

// Rutas
import { Router } from '@angular/router';

// Servicios y Guards
import { AuthenticationService } from './services/auth/authentication.service';
import { AuthGuard } from './guards/auth/auth.guard';

// Paginas
import { LoginComponent } from './pages/login/login.component';

// Componentes
import { SidebarComponent } from './components/shared/sidebar/sidebar.component';
import { AuthHttp } from './services/http/http.service';
import { FooterbarComponent } from './components/shared/footerbar/footerbar.component';
import { DashboardModule } from './modules/dashboard/dashboard.module';
import { AdminBarComponent } from './components/shared/admin-bar/admin-bar.component';
import { ArbolService } from './services/arbol/arbol.service';
import { EmpresaService } from './modules/empresa/services/empresa.service';
import { ProveedoresService } from './modules/proveedores/services/proveedores.service';
import { StatusService } from '@app/services/status';
import { HttpErrorInterceptor } from './interceptors/httpError.interceptor';
import { SsoComponent } from './pages/sso/sso.component';
import { PermissionGuard } from './guards/permission-guard.guard';
import { InfoconfiguracionService } from './services/infoconfiguracion/infoconfiguracion.service';
import { MiddlewareGuard } from './guards/middleware.guard';
import { StateableGuard } from './guards/stateable-guard.guard';
import { VersionService } from './services/version/version.service';
import { JWTInterceptor } from './interceptors/jwt.interceptor';
import { VersionInterceptor } from './interceptors/version.interceptor';
import { SharedModule } from '@module-shared/shared.module';
import { FaqService } from './services/faq/faq.service';
import { NZ_I18N, es_ES, NZ_DATE_CONFIG, NZ_DATE_LOCALE } from 'ng-zorro-antd/i18n';
import esDateLocale from 'date-fns/locale/es';

import es from '@angular/common/locales/es';
import { GrupoService } from './modules/grupo/services/grupo.service';
import { AGCustomTooltip } from './components/shared/agToolTip/agTooltip.component';
import { TicketBaiInfoService } from './services/facturaE/TicketBaiInfoService';
import { GabinetesComponent } from './modules/configuracion/agenda/gabinetes/component/gabinetes.component';
import { StorageService } from '@global-service/storage';
import { GeneralApiService } from '@global-service/api';
import { ConfiguracionService } from '@global-service/configuracion';
import { EventosCompartidosService } from '@global-service/eventosCompartidos/eventosCompartidos.service';
import { AdminGuard } from './guards/admin.guard';
import { AppRoutingModule } from './routes/app.routing';
import { EmpresaCentralizadaService } from './services/empresaCentralizada/empresaCentralizada.service';
import { SucursalService } from './modules/sucursal/services/sucursal.service';
import { SucursalService as EmpresaSucursalService } from './modules/configuracion/empresa/sucursales/services/sucursal.service';
import { SucursalesService } from './modules/configuracion/empresa/sucursales/services/sucursales.service';
import { SociedadService } from './modules/configuracion/empresa/sociedades/services/sociedad.service';
import { SociedadesService } from './modules/configuracion/empresa/sociedades/services/sociedades.service';
import { FormularioService } from './services/formulario/formulario.service';
import { RegimenesFiscalesService } from './modules/configuracion/empresa/regimenes-fiscales/services/regimenes-fiscales.service';
import { GabineteService } from './modules/configuracion/agenda/gabinetes/services/gabinetes.service';
import { VersionInfoComponent } from "@app/pages/version/version-info.component";
import * as Sentry from "@sentry/angular";

registerLocaleData(es);
// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

export function initializeAppInjector(injector: Injector): () => void {
  return () => AppInjector.setInjector(injector);
}

@NgModule({ declarations: [AppComponent, LoginComponent, SidebarComponent, FooterbarComponent, AdminBarComponent, SsoComponent, AGCustomTooltip, GabinetesComponent, VersionInfoComponent],
    exports: [TranslateModule, SharedModule],
    bootstrap: [AppComponent], imports: [BrowserModule,
        AppRoutingModule,
        FormsModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient],
            },
        }),
        ToastNoAnimationModule.forRoot({
            preventDuplicates: true,
            autoDismiss: true,
        }),
        DashboardModule,
        SharedModule], providers: [
        SucursalService,
        EmpresaSucursalService,
        SucursalesService,
        SociedadService,
        SociedadesService,
        FormularioService,
        RegimenesFiscalesService,
        GabineteService,
        {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler({
                showDialog: false,
            }),
        }, {
            provide: Sentry.TraceService,
            deps: [Router],
        },
        provideAppInitializer(() => { inject(Sentry.TraceService); }),
        provideAppInitializer(() => initializeAppInjector(inject(Injector))()),
        AuthGuard,
        AdminGuard,
        PermissionGuard,
        MiddlewareGuard,
        StateableGuard,
        AuthenticationService,
        AuthHttp,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpErrorInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: JWTInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: VersionInterceptor,
            multi: true,
        },
        FaqService,
        EmpresaService,
        GrupoService,
        ArbolService,
        ProveedoresService,
        StatusService,
        InfoconfiguracionService,
        VersionService,
        DatePipe,
        TitleCasePipe,
        TicketBaiInfoService,
        StorageService,
        GeneralApiService,
        ConfiguracionService,
        EventosCompartidosService,
        EmpresaCentralizadaService,
        { provide: NZ_I18N, useValue: es_ES },
        { provide: NZ_DATE_LOCALE, useValue: esDateLocale },
        { provide: NZ_DATE_CONFIG, useValue: { firstDayOfWeek: 1 } },
        provideHttpClient(withInterceptorsFromDi()),
    ] })
export class AppModule {}
